// ============================================================
// Per-checkpoint coaching narrative (CP1–CP10)
//
// Each CP carries five voices:
//   stage           — which of the seven sales stages the AE is in
//   purpose         — why this checkpoint matters and what it controls
//   inConversation  — what this looks like in the conversation; the order
//                     to ask the questions
//   onTheFence      — guidance when the answer feels 50/50
//   overallImpact   — how the score here changes the whole opportunity
//
// The seven stages of the Mi6 sales process:
//   1. Introduction
//   2. Basic discovery
//   3. Formal discovery
//   4. Scoping
//   5. Pain   (establishes that the customer has recognized pain for their use case)
//   6. Finance         (can they buy it; budget; ROI)
//   7. Legal (sales stage 7 — paper path, written commitment)
//
// 10-checkpoint model — canonical (locked 2026-05-19):
//   CP1  Project                        — Basic discovery        — hard-kill
//   CP2  Need                           — Formal discovery       — hard-kill
//   CP3  Pain                           — Pain                   — hard-kill (conditional)
//   CP4  Fit                            — Formal discovery       — hard-kill
//   CP5  Budget                         — Formal discovery       — hard-kill
//   CP6  Timing                         — Formal discovery       — soft
//   CP7  Authority                      — Scoping                — hard-kill
//   CP8  Org Structure and Influence Map     — Scoping                — soft
//   CP9  Compliance — IT Internal and Industry-Specific      — Closing                — soft
//   CP10 Legal — Contracts, SLAs, Terms            — Closing                — hard-kill
//
// Pair groupings (UI):    CP1-CP2, CP3-CP4, CP5-CP6, CP7-CP8, CP9-CP10
// Qualification half:     CP1-CP5
// Closing half:           CP6-CP10
// Floor mapping:          CP1 = reception, CP10 = rooftop
//
// Style rules per Bryan: short, direct, senior bench, no exclamation
// points, no emojis, him/her pronouns. Never "rep" — use "AE" or "BDR".
// "We do not chase the yes. We invite the no."
// "Going from 'I think' to 'I know' is the whole job."
// ============================================================

export interface CpCoaching {
  cp: number;
  title: string;
  hardKill: boolean;
  stage: string;
  purpose: string;
  inConversation: string;
  onTheFence: string;
  overallImpact: string;
}

export const CP_COACHING: Record<number, CpCoaching> = {
  1: {
    cp: 1,
    title: "Project",
    hardKill: true,
    stage: "Basic discovery",
    purpose:
      "Confirm there is a real project — a named initiative, a sponsor, and a budget cycle this falls into. New logo or expansion both count; an idea, a wish, or a tire-kick does not. Without a project, everything below is decoration.",
    inConversation:
      "You are past introductions and inside basic discovery. Ask whether there is a named project on her plan this year. If yes, ask whether it is a new logo or an expansion of existing work — that one answer changes the motion. Then ask the why-now: what moved this from someday to this quarter. Strong answers come back with names, dates, and a publicly stated commitment. Weak answers come back as 'we are exploring' or 'we are looking into it' — that is not a project yet.",
    onTheFence:
      "If she can describe the goal but cannot name the project, the sponsor, or the budget cycle, mark Orange and assign a hunt item to confirm with the operator. Do not score Green on enthusiasm. We do not chase the yes; we invite the no.",
    overallImpact:
      "CP1 is hard-kill. A red here drops the whole opportunity to OMITTED no matter how strong the rest of the deal looks. Until you can answer this in her own words, the deal does not exist.",
  },
  2: {
    cp: 2,
    title: "Need",
    hardKill: true,
    stage: "Formal discovery",
    purpose:
      "Confirm the buyer recognizes a need we can address. Not our interpretation of their situation — their own words, unprompted, describing a gap or a problem they want to close.",
    inConversation:
      "Ask open-ended questions about what they are trying to accomplish and what is standing in the way. Listen for language that signals recognized need: 'we need to,' 'we have been struggling with,' 'we have not been able to.' Write those phrases down verbatim. A need that the rep inferred but the buyer never stated is not a confirmed Need.",
    onTheFence:
      "If the buyer is curious but has not stated a recognized need, mark Orange. Curiosity is not Need. A buyer who agrees with your framing but has not independently described the problem is Orange, not Green. Green requires their words, not yours.",
    overallImpact:
      "CP2 is hard-kill. Without a recognized need from the buyer, there is nothing to solve and no reason to continue. Every downstream checkpoint — Pain, Budget, Authority — assumes Need is real. If Need collapses late in the deal, the deal collapses with it.",
  },
  3: {
    cp: 3,
    title: "Budget",
    hardKill: true,
    stage: "Pain",
    purpose:
      "Establish that pain exists — recognized by the buyer, quantified in their own words, corroborated by a second source. Pain named by one contact is fragile pain. Pain that two people describe the same way, with a number attached, is actionable.",
    inConversation:
      "Open with an open-ended pain question: 'What is the single most expensive problem you are living with right now in this area?' Write down the exact phrasing — do not paraphrase. Then ask what breaks if they do nothing for another twelve months. Then press for the number: annualized impact, headcount cost, missed revenue, or compliance exposure. Corroborate the number with a second stakeholder before marking Green. If only one person can describe the pain, mark Orange and add a hunt item to confirm with a second contact.",
    onTheFence:
      "If the buyer describes the pain but cannot put a number on it, mark Orange and add a hunt item to get the figure from finance or operations. Do not invent a number to move the score. Going from 'I think' to 'I know' is the whole job. No pain confirmed means no place in the forecast.",
    overallImpact:
      "CP3 is hard-kill conditional. The trigger is narrow: only a confirmed no-pain finding — where the buyer states there is no problem worth spending money on — kills the deal at this checkpoint. Without CP3 cleared, the account does not appear in forecast and does not count toward quota attainment.",
  },
  4: {
    cp: 4,
    title: "Timeline",
    hardKill: true,
    stage: "Formal discovery",
    purpose:
      "Real money, named source, validated line item. Budget without a name is a wish. The named owner and the named line item are what separate a deal from a hope.",
    inConversation:
      "Inside formal discovery, after pain is quantified. Ask whether there is a budget number, who owns it, and which line item it lives in. If the answer is 'we will find the budget,' that is Orange, not Green — write that down verbatim. Then ask the approval threshold: who can sign at this deal size; what is the next ceiling above that. A clean answer comes back with a name, a number, and a cost center.",
    onTheFence:
      "If the budget number is named but the owner is not, mark Orange. If the owner is named but the line item is not, mark Orange. We do not score Green on a champion's promise that 'the money is there.' We score Green when the owner has confirmed it.",
    overallImpact:
      "CP4 is hard-kill. No budget, no deal — and a budget that lives only in the champion's head is the same as no budget. A red here flags forecast risk immediately and pulls the opportunity out of COMMIT.",
  },
  5: {
    cp: 5,
    title: "Authority — Project / Initiative",
    hardKill: false,
    stage: "Formal discovery",
    purpose:
      "Both the decision window AND the implementation window — both real, both confirmed in writing. One date is half a timeline. 'They want it done by year-end' is hope, not a timeline.",
    inConversation:
      "Formal discovery, alongside Budget. Ask the decision date first — when must she make the call. Then ask the go-live date — when must the system be running. Then ask what is driving each: contract expiry, fiscal year, product launch, compliance deadline, board review. Confirm both dates are mutually agreed, not your assumption.",
    onTheFence:
      "If she has a target date but no formal commitment, mark Orange and label it as a target only. Coach the AE to confirm in writing — an email back from her saying 'yes, we are aiming at this date and this go-live' is the bar. Without that, the timeline is yours, not hers.",
    overallImpact:
      "CP5 is soft-kill. A red here does not kill the deal but pulls it out of COMMIT and PROBABLE. Where it matters most is bucket placement — without a firm timeline, this opportunity should not carry a forecast date.",
  },
  6: {
    cp: 6,
    title: "Technical and Financial Sign-off",
    hardKill: true,
    stage: "Scoping",
    purpose:
      "Who actually signs — economic, technical, security, legal — named, met, and dated. If the AE has not spoken to the signer, the signer is not real yet. Champions who have not introduced you to the signer are not real champions yet.",
    inConversation:
      "Scoping stage. Ask for the name and title of the final signer. Then ask the date of her last conversation with that signer. Then walk the four lanes: financial, technical, security, legal — name a person in each, and the last contact date. Last, ask whether the champion is coached: can she sell this when you are not in the room; has she done one internal pitch.",
    onTheFence:
      "If the signer is named but the AE has never spoken to her, mark Orange and assign the introduction as a hunt item. A 'committee' answer is not authority — keep pushing until you have a name. Relationship-only access loses to procurement every time.",
    overallImpact:
      "CP6 is hard-kill. Without a confirmed economic buyer the deal cannot move to COMMIT. Without all four lanes named, the deal cannot survive the back half of the closing motion — it will surface a surprise approver in CP9 or CP10 and slip a quarter.",
  },
  7: {
    cp: 7,
    title: "Org Structure and Influence Map",
    hardKill: false,
    stage: "Formal discovery",
    purpose:
      "Champion, supporter, influencer, detractor — every deal has all four. Map the political, personal, and professional agendas. If you only see champions, you have not looked hard enough.",
    inConversation:
      "Formal discovery, layered on top of Authority. Start with the Project Lead — every project has one, always present, and the title on the org chart may not match the actual authority. The Project Lead is the singular validator who confirms the picture the rep has assembled. Then ask the AE to name the champions (anyone helping us — executive sponsor, liaison, sponsor — plural, optional, never replacing the Project Lead), the supporter, the influencer, and the likely detractor. If she cannot name a detractor, push: who would lose budget, headcount, or status if this goes through. Then ask the informal power broker whose opinion bends the room without holding formal title. Last, ask the agenda layer: what is each person's personal stake, professional stake, political stake.",
    onTheFence:
      "If she names champions and supporters but no detractors, mark Orange. Every initiative threatens someone. Coach the AE to find the detractor and build a neutralization plan — not a confrontation plan, a quiet-the-room plan. A mutual close plan countersigned by the champion takes this from Orange to Green.",
    overallImpact:
      "CP7 is soft-kill. A red here does not kill the deal alone, but combined with a soft red on Competition (CP8) it almost guarantees a slip. The deals that die two weeks before signature die here — an unmapped detractor surfaces and the room goes quiet.",
  },
  8: {
    cp: 8,
    title: "Competitive Position",
    hardKill: false,
    stage: "Scoping",
    purpose:
      "Where you stand vs alternatives in her head, evaluated not assumed. Ask her to rank the options. Listen for what she does not say. The do-nothing option is always in the deal — name it explicitly.",
    inConversation:
      "Scoping stage. Ask who else is in the deal — including the do-nothing option and build-vs-buy. Then ask where you stand in her evaluation — leading, tied, behind. Then ask her to describe your differentiation back to you, in her own words. If she cannot, you have not won her yet. Last, ask whether the decision criteria were shaped by you or handed to you cold — cold criteria are losses not yet booked.",
    onTheFence:
      "If you are leading on relationship but she cannot articulate why you are technically better, mark Orange. Relationship wins lose to procurement. Coach the AE to build the differentiation in her words — give the champion a battle card and have her use it once internally before you score Green.",
    overallImpact:
      "CP8 is soft-kill. A red here is recoverable with battle-card deployment and champion enablement. Combined with a soft red on Authority (CP7) or Timeline (CP5), the deal slips out of COMMIT and often out of PROBABLE.",
  },
  9: {
    cp: 9,
    title: "Compliance — IT Internal and Industry-Specific",
    hardKill: true,
    stage: "Compliance — IT Internal and Industry-Specific",
    purpose:
      "Payment terms, capex/opex classification, approval thresholds, and the sourcing process — confirmed in writing. A handshake number is not finance. Procurement cycle time is part of the close-date math, not an afterthought.",
    inConversation:
      "Finance stage. The customer has seen the demo, validated the budget, and named the signer; now the question is whether the paper path is open. Ask the spend classification — capex, opex, net-new — and confirm she agrees. Then payment terms, approval thresholds, sourcing process. Then is procurement engaged: named lead, cycle modeled into the close date. Last, the ROI case: built with her, in her numbers, signed by the champion.",
    onTheFence:
      "If procurement has not been engaged yet, mark Orange and put procurement engagement on the close plan as a dated milestone. If the ROI case is yours, not hers, mark Orange. The ROI case has to live inside her finance team's spreadsheet, not yours.",
    overallImpact:
      "CP9 is hard-kill. Without a real sourcing path the deal cannot close in the named quarter. The most common late-stage slip in this model is a CP9 that was scored Green on a champion promise and reds out the week procurement opens the file.",
  },
  10: {
    cp: 10,
    title: "Legal — Contracts, SLAs, Terms",
    hardKill: true,
    stage: "Legal — Contracts, SLAs, Terms",
    purpose:
      "MSA in flight, redlines tracked, counsel named on both sides, paper path open. If legal has not seen it, you do not have a deal. This is the cleanest hard-kill in the model — and the easiest to fix once everything else is green.",
    inConversation:
      "Legal stage, the final gate. Ask whether the MSA is in place or in active negotiation. Then redlines: started, exchanged, completed, status tracked. Then security review: complete or scheduled with a date. Last, model legal review days into the close-date math explicitly — procurement plus legal is a real number, not a buffer.",
    onTheFence:
      "There is no fence here. Either you have something in writing or you have a verbal commitment. Verbal is Orange at best; Green requires a document with both counsels named on it. Score on the artifact, not on the conversation.",
    overallImpact:
      "CP10 is hard-kill. Without written commitment the deal cannot move to COMMIT bucket. The good news: this is the cleanest fix in the model. Once everything else is green, CP10 is an execution sprint, not a discovery exercise.",
  },
};

// ============================================================
// The seven sales stages — exposed for headers, badges, and the
// stage progression strip on the Question Bank.
// ============================================================
export const SALES_STAGES = [
  { num: 1, label: "Introduction" },
  { num: 2, label: "Basic discovery" },
  { num: 3, label: "Formal discovery" },
  { num: 4, label: "Scoping" },
  { num: 5, label: "Pain" },
  { num: 6, label: "Compliance — IT Internal and Industry-Specific" },
  { num: 7, label: "Legal" },
] as const;
